import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Collapse,
  Space,
  Typography,
  Card,
  Empty,
  Modal,
  Button,
  message,
  Spin,
  Checkbox,
  FloatButton,
  Tag,
  Divider,
  Input,
  List,
  Popconfirm,
} from "antd";
import {
  CheckCircleOutlined,
  FileTextOutlined,
  IdcardOutlined,
  HomeOutlined,
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import axios from "axios";
import {
  GET_GEO_ALL,
  HEADERS_EXPORT_BEN,
  POST_DOC_RESEND,
  POST_DOC_STATUS,
  POST_LAWSUIT_DETAIL,
} from "../../../API/apiUrls";
import dayjs from "dayjs";
import AddAddressTr from "./AddAddressTr";
import { Link } from "react-router-dom";
import DebtPayment from "./DebtPayment";

const CheckingDocuments = ({ open, close, dataDefault, func, status }) => {
  const userId = parseInt(localStorage.getItem("USER_ID"));
  const [loading, setLoading] = useState(false);
  const [checkedList, setCheckedList] = useState({});
  const scrollRef = useRef(null);
  const [dataLoadLawSuit, setDataLoadLawSuit] = useState(null);
  const [dataAsset, setDataAsset] = useState([]);
  const [dataWCC, setDataWCC] = useState([]);
  const [activeKey, setActiveKey] = useState([]);
  const [isModalAddAddressTr, setIsModalAddAddressTr] = useState(false);
  const [geoRawData, setGeoRawData] = useState(null);
  const [memoData, setMemoData] = useState(null);
  const [isModalDebtPayment, setIsModalDebtPayment] = useState(false);
  const [verifyList, setVerifyList] = useState({}); // ✅ เพิ่ม state สำหรับ checkbox ฝั่งขวา
  const [newMemoText, setNewMemoText] = useState(""); // หมายเหตุใหม่

  // ✅ โหลด GEO Data (ครั้งเดียว)
  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadGeoData = async () => {
      try {
        const res = await axios.get(GET_GEO_ALL, {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        });

        if (isMounted) {
          setGeoRawData(res.data);
          console.log("✅ geoRawData loaded");
        }
      } catch (error) {
        if (error.name !== "CanceledError" && isMounted) {
          console.error("❌ Error loading geo data:", error);
        }
      }
    };

    loadGeoData();
    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  // ✅ โหลดข้อมูล Lawsuit
  const loadLawsuitData = useCallback(async () => {
    if (!dataDefault) return null;

    setLoading(true);
    const controller = new AbortController();

    try {
      console.log("📋 Loading lawsuit data for:", dataDefault);

      const lawsuitRes = await axios.post(
        POST_LAWSUIT_DETAIL,
        {
          CONTNO: dataDefault.CONTNO,
          loanId: dataDefault.loanId,
          lawsuitId: dataDefault.lawsuitId || 0,
        },
        {
          headers: HEADERS_EXPORT_BEN,
          signal: controller.signal,
        },
      );

      if (lawsuitRes?.data) {
        console.log("✅ Lawsuit data loaded:", lawsuitRes.data);
        oldDataLawsuitDoc(lawsuitRes.data);
        setDataLoadLawSuit(lawsuitRes.data.lawsuit);
        setDataAsset(lawsuitRes.data.asset || []);
        setDataWCC(lawsuitRes.data.wcc || []);
        setMemoData(lawsuitRes.data.note || []);
        return lawsuitRes.data.lawsuit;
      } else {
        throw new Error("ไม่พบข้อมูลคดี");
      }
    } catch (error) {
      if (error.name !== "CanceledError") {
        console.error("❌ Error loading lawsuit:", error);
        const errorMsg = error.response?.data?.message || error.message;
        message.error(`เกิดข้อผิดพลาด: ${errorMsg}`);
        setDataLoadLawSuit(null);
        setDataAsset([]);
        setDataWCC([]);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, [dataDefault]);

  // ✅ โหลดข้อมูลเมื่อเปิด Modal
  useEffect(() => {
    if (!open) return;

    // Reset checked list เมื่อเปิด modal
    setCheckedList({});
    loadLawsuitData();
  }, [open, loadLawsuitData]);

  // ⭐ NEW: โหลดข้อมูลเดิมเมื่อ status === 3
  const oldDataLawsuitDoc = (rawData) => {
    let initialCheckedList;
    if ((status === 3 || status === 6) && rawData?.lawsuit?.workLawsuitDoc) {
      const doc = rawData.lawsuit.workLawsuitDoc;
      const loanOld = rawData.lawsuit.loanOld; // ✅ เพิ่มตัวแปรนี้

      console.log("📝 โหลดข้อมูลเดิมจาก workLawsuitDoc:", doc);
      console.log("📝 ข้อมูล loanOld:", loanOld);

      // สร้าง initial checked list
      initialCheckedList = {
        contract: doc.docContno || false,
        property_docs: doc.docMainAsset || false,
        debtor_card: doc.docDebtorCard || false,
      };

      // ✅ โหลด verify list (ถ้ามี field ในฐานข้อมูล)
      const initialVerifyList = {
        contract_verify: doc.docContnoLawyer || false,
        property_docs_verify: doc.docMainAssetLawyer || false,
        debtor_card_verify: doc.docDebtorCardLawyer || false,
        notice_verify: doc.docNotifyLawyer || false,
        termination_verify: doc.docWccLawyer || false,
        id_cards_verify: doc.docCopyCardLawyer || false,
        tr14_verify: doc.docAddressTrLawyer || false,
      };

      if (doc.docCopyCard && rawData.asset) {
        // ✅ Tick ทุกคนโดยไม่ต้องแยกกลุ่ม
        rawData.asset.forEach((person) => {
          if (person.customerId) {
            initialCheckedList[`id_card_${person.customerId}`] = true;
          }
        });
      }

      if (doc.docAddressTr && loanOld) {
        // Tick ที่อยู่ ทร.14 ของผู้เช่าซื้อ
        if (loanOld.addressTr && loanOld.addressTr.length > 0) {
          const address = loanOld.addressTr[0];
          initialCheckedList[
            `tr14_${loanOld.CUSTOMER_ID}_${address.addrTrId}`
          ] = true;
        }

        // Tick ที่อยู่ ทร.14 ของผู้ค้ำทุกคน
        if (loanOld.guarantors && loanOld.guarantors.length > 0) {
          loanOld.guarantors.forEach((guarantor) => {
            if (guarantor.addressTr && guarantor.addressTr.length > 0) {
              const address = guarantor.addressTr[0];
              initialCheckedList[
                `tr14_${guarantor.CUSTOMER_ID}_${address.addrTrId}`
              ] = true;
            }
          });
        }
      }

      // 2. ถ้า docWcc = true ให้ tick บอกเลิกทุกรายการ
      if (doc.docWcc && rawData.wcc) {
        rawData.wcc.forEach((item) => {
          initialCheckedList[`termination_${item.id}`] = true;
        });
      }

      // 3. ถ้า docNotify มีค่า ให้ tick บอกกล่าวทุกรายการ
      if (doc.docNotify && rawData.wcc) {
        rawData.wcc.forEach((item) => {
          initialCheckedList[`notice_${item.id}`] = true;
        });
      }

      setCheckedList(initialCheckedList);
      setMemoData(doc.docMark || null);
      setVerifyList(initialVerifyList);

      console.log("✅ โหลดข้อมูลเดิมเสร็จสิ้น:", initialCheckedList);
    } else {
      initialCheckedList = {
        contract: true,
        property_docs: true,
        debtor_card: true,
      };
      setCheckedList(initialCheckedList);
    }
  };

  const handleCancel = () => {
    close();
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // ✅ รวบรวมสถานะ checkbox ทั้งหมด
      const checkboxStatus = {
        // Checkbox เดี่ยว
        contract: checkedList["contract"] || false,
        property_docs: checkedList["property_docs"] || false,
        debtor_card: checkedList["debtor_card"] || false,
        // loan_application: checkedList["loan_application"] || false,
        // Checkbox ใหญ่ (จาก useMemo)
        notice: noticeChecked,
        termination: terminationChecked,
        id_cards: idCardsChecked,
        tr14: tr14Checked,
      };

      console.log("📊 สถานะ checkbox ทั้งหมด:", checkboxStatus);

      // ✅ สร้าง payload สำหรับ API
      const payload = {
        docId:
          status === 3 || status === 6
            ? dataLoadLawSuit.workLawsuitDoc.docId
            : 0,
        lawsuitId: dataDefault.lawsuitId,
        loanId: dataDefault.loanId,
        docContno: checkboxStatus.contract ? true : false, // สัญญา
        docNotify: checkboxStatus.notice ? true : false, // บอกกล่าว
        docWcc: checkboxStatus.termination ? true : false, // บอกเลิก
        docMainAsset: checkboxStatus.property_docs ? true : false, // เอกสารหลักทรัพย์
        docDebtorCard: checkboxStatus.debtor_card ? true : false, // การ์ดลูกหนี้
        docCopyCard: checkboxStatus.id_cards ? true : false, // สำเนาบัตร
        docAddressTr: checkboxStatus.tr14,
        userId: userId,
        docMark: newMemoText,
      };

      console.log("📌 Payload ส่ง API:", payload);
      let apiUse = status === 6 ? POST_DOC_RESEND : POST_DOC_STATUS;
      const result = await postDocStatus(payload, apiUse);

      if (func) {
        func({
          action: "refresh",
          data: result,
        });
      }

      message.success(
        status === 3 || status === 6 ? "แก้ไขสำเร็จ" : "บันทึกสำเร็จ",
      );
      close();
    } catch (err) {
      console.error("❌ Error saving:", err);
      message.error(
        status === 3 || status === 6 ? "แก้ไขไม่สำเร็จ" : "บันทึกไม่สำเร็จ",
      );
    } finally {
      setLoading(false);
    }
  };

  // ✅ ฟังก์ชันส่งข้อมูลไป API
  const postDocStatus = async (payload, apiUse) => {
    console.log("📤 POST_DOC_STATUS with data:", payload);

    try {
      const res = await axios.post(apiUse, payload, {
        headers: HEADERS_EXPORT_BEN,
      });

      const { status, data } = res;

      if (status === 200) {
        console.log("✅ API Response:", data);
        return data;
      }
    } catch (error) {
      const status = error?.response?.status;
      const serverMsg =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error.message;

      if (status === 404) {
        message.error("ไม่พบปลายทาง (404)");
      } else if (status === 400) {
        message.error(`ข้อมูลไม่ถูกต้อง: ${serverMsg}`);
      } else if (status === 500) {
        message.error("เกิดข้อผิดพลาดจากเซิร์ฟเวอร์ (500)");
      } else {
        message.error(
          `ไม่สามารถส่งข้อมูลได้${status ? ` (${status})` : ""}: ${serverMsg}`,
        );
      }

      console.error("❌ postDocStatus error:", error);
      throw error;
    }
  };

  const handleCheckboxChange = useCallback((id, checked) => {
    setCheckedList((prev) => ({
      ...prev,
      [id]: checked,
    }));
  }, []);

  // ✅ Handler: Callback จาก AddAddressTr
  const handleLoad = useCallback(
    async (data) => {
      console.log("📥 Callback from AddAddressTr:", data);

      if (data?.action === "refresh") {
        // ⭐ โหลดเฉพาะ lawsuit ใหม่
        await loadLawsuitData();
        message.success("อัพเดทข้อมูลที่อยู่ ทร.14 สำเร็จ");
      }
    },
    [loadLawsuitData],
  );

  // ✅ ฟังก์ชันแปลง code เป็นชื่อ GEO
  const getGeoNameFromCode = useCallback(
    (provCode, distCode, subDistCode) => {
      let provName = null;
      let distName = null;
      let subDistName = null;

      if (geoRawData) {
        const province = geoRawData.province?.find(
          (p) => String(p.provCode) === String(provCode),
        );
        provName = province?.provDesc || null;

        if (province && distCode) {
          const district = geoRawData.district?.find(
            (d) =>
              String(d.provCode) === String(provCode) &&
              String(d.distCode) === String(distCode),
          );
          distName = district?.distDesc || null;
        }

        if (province && distCode && subDistCode) {
          const subDistrict = geoRawData.subDistrict?.find(
            (s) =>
              String(s.provCode) === String(provCode) &&
              String(s.distCode) === String(distCode) &&
              String(s.subDistCode) === String(subDistCode),
          );
          subDistName = subDistrict?.subDistDesc || null;
        }
      }

      return { provName, distName, subDistName };
    },
    [geoRawData],
  );

  // ✅ ฟังก์ชันสร้างที่อยู่เต็ม
  const formatFullAddress = useCallback(
    (address) => {
      const parts = [
        address.houseNumber,
        address.soi,
        address.road,
        address.village,
      ].filter(Boolean);

      const geoNames = getGeoNameFromCode(
        address.province,
        address.district,
        address.subdistrict,
      );

      const geoParts = [
        geoNames.subDistName,
        geoNames.distName,
        geoNames.provName,
        address.zipcode,
      ].filter(Boolean);

      return {
        address: parts.join(" "),
        geo: geoParts.join(" "),
        fullAddress: [...parts, ...geoParts].join(" "),
        province: geoNames.provName,
        district: geoNames.distName,
        subdistrict: geoNames.subDistName,
      };
    },
    [getGeoNameFromCode],
  );

  // ✅ ตรวจสอบว่าเป็นที่อยู่ที่ไม่เกิน 1 เดือน
  // const isWithinOneMonth = useCallback((createdAt) => {
  //   if (!createdAt) return false;
  //   const oneMonthAgo = dayjs().subtract(1, "month");
  //   return dayjs(createdAt).isAfter(oneMonthAgo);
  // }, []);

  // ✅ สร้างรายการ ทร.14 พร้อมกรองตามวันที่ (เฉพาะที่อยู่ล่าสุด 1 ที่อยู่)
  // const getTr14List = useCallback(() => {
  //   if (!dataLoadLawSuit?.loanOld) return [];

  //   const list = [];
  //   const loanOld = dataLoadLawSuit.loanOld;

  //   // ผู้เช่าซื้อ
  //   if (loanOld.addressTr && loanOld.addressTr.length > 0) {
  //     const borrower = dataAsset.find((item) => item.garno === 0);
  //     const borrowerMoney = borrower?.money;

  //     // ⭐ กรองเฉพาะที่อยู่ที่ไม่เกิน 1 เดือน
  //     const validAddresses = loanOld.addressTr.filter((addr) =>
  //       isWithinOneMonth(addr.CreatedAt),
  //     );

  //     // ⭐ เลือกเฉพาะที่อยู่ล่าสุด (1 ที่อยู่)
  //     if (validAddresses.length > 0) {
  //       const latestAddress = validAddresses.reduce((latest, current) => {
  //         return dayjs(current.CreatedAt).isAfter(dayjs(latest.CreatedAt))
  //           ? current
  //           : latest;
  //       });

  //       list.push({
  //         customerId: loanOld.CUSTOMER_ID,
  //         type: "borrower",
  //         garno: 0,
  //         name: borrower
  //           ? `${borrower.SNAM}${borrower.NAME1} ${borrower.NAME2}`
  //           : "ผู้เช่าซื้อ",
  //         addressTr: [
  //           {
  //             ...latestAddress,
  //             ...formatFullAddress(latestAddress),
  //           },
  //         ],
  //         money: borrowerMoney,
  //       });
  //     }
  //   }

  //   // ผู้ค้ำประกัน
  //   if (loanOld.guarantors && loanOld.guarantors.length > 0) {
  //     loanOld.guarantors.forEach((guarantor) => {
  //       if (guarantor.addressTr && guarantor.addressTr.length > 0) {
  //         const guarantorAsset = dataAsset.find(
  //           (item) => item.garno === guarantor.garno,
  //         );
  //         const guarantorMoney = guarantorAsset?.money;

  //         // ⭐ กรองเฉพาะที่อยู่ที่ไม่เกิน 1 เดือน
  //         const validAddresses = guarantor.addressTr.filter((addr) =>
  //           isWithinOneMonth(addr.CreatedAt),
  //         );

  //         // ⭐ เลือกเฉพาะที่อยู่ล่าสุด (1 ที่อยู่)
  //         if (validAddresses.length > 0) {
  //           const latestAddress = validAddresses.reduce((latest, current) => {
  //             return dayjs(current.CreatedAt).isAfter(dayjs(latest.CreatedAt))
  //               ? current
  //               : latest;
  //           });

  //           list.push({
  //             customerId: guarantor.CUSTOMER_ID,
  //             type: "guarantor",
  //             garno: guarantor.garno,
  //             name: guarantorAsset
  //               ? `${guarantorAsset.SNAM}${guarantorAsset.NAME1} ${guarantorAsset.NAME2}`
  //               : `ผู้ค้ำคนที่ ${guarantor.garno}`,
  //             relation: guarantor.RELATN,
  //             addressTr: [
  //               {
  //                 ...latestAddress,
  //                 ...formatFullAddress(latestAddress),
  //               },
  //             ],
  //             money: guarantorMoney,
  //           });
  //         }
  //       }
  //     });
  //   }

  //   return list;
  // }, [dataLoadLawSuit, dataAsset, isWithinOneMonth, formatFullAddress]);

  //เอาทั้งหมด
  const getTr14List = useCallback(() => {
    if (!dataLoadLawSuit?.loanOld) return [];

    const list = [];
    const loanOld = dataLoadLawSuit.loanOld;

    // ⭐ Helper: หาที่อยู่ล่าสุด
    const getLatestAddress = (addresses) => {
      if (!addresses || addresses.length === 0) return null;
      return addresses.reduce((latest, current) =>
        dayjs(current.CreatedAt).isAfter(dayjs(latest.CreatedAt))
          ? current
          : latest,
      );
    };

    // ═══════════════════════════════════════════
    // ผู้เช่าซื้อ
    // ═══════════════════════════════════════════
    if (loanOld.addressTr && loanOld.addressTr.length > 0) {
      const borrower = dataAsset.find((item) => item.garno === 0);
      const latestAddress = getLatestAddress(loanOld.addressTr); // ✅ ไม่ filter

      if (latestAddress) {
        list.push({
          customerId: loanOld.CUSTOMER_ID,
          type: "borrower",
          garno: 0,
          name: borrower
            ? `${borrower.SNAM}${borrower.NAME1} ${borrower.NAME2}`
            : "ผู้เช่าซื้อ",
          addressTr: [
            {
              ...latestAddress,
              ...formatFullAddress(latestAddress),
            },
          ],
          money: borrower?.money,
        });
      }
    }

    // ═══════════════════════════════════════════
    // ผู้ค้ำประกัน (ต้องแก้ตรงนี้!)
    // ═══════════════════════════════════════════
    if (loanOld.guarantors && loanOld.guarantors.length > 0) {
      loanOld.guarantors.forEach((guarantor) => {
        if (guarantor.addressTr && guarantor.addressTr.length > 0) {
          const guarantorAsset = dataAsset.find(
            (item) => item.garno === guarantor.garno,
          );

          // ✅ แก้ตรงนี้! - ไม่ filter, เอาล่าสุดเลย
          const latestAddress = getLatestAddress(guarantor.addressTr);

          if (latestAddress) {
            list.push({
              customerId: guarantor.CUSTOMER_ID,
              type: "guarantor",
              garno: guarantor.garno,
              name: guarantorAsset
                ? `${guarantorAsset.SNAM}${guarantorAsset.NAME1} ${guarantorAsset.NAME2}`
                : `ผู้ค้ำคนที่ ${guarantor.garno}`,
              relation: guarantor.RELATN,
              addressTr: [
                {
                  ...latestAddress,
                  ...formatFullAddress(latestAddress),
                },
              ],
              money: guarantorAsset?.money,
            });
          }
        }
      });
    }

    console.log("📋 tr14List result:", list); // ⭐ Debug
    return list;
  }, [dataLoadLawSuit, dataAsset, formatFullAddress]); // ✅ ลบ isWithinOneMonth

  // 1. สำเนาบัตรประชาชน - เช็คจากจำนวน customer + guarantor
  const idCardsChecked = useMemo(() => {
    // ✅ ผู้เช่าซื้อหลัก
    const mainBorrower = dataAsset.find(
      (item) => item.garno === 0 && !item.relationCustomerId,
    );

    // ✅ คนที่เกี่ยวข้องทั้งหมด (คู่สมรส/ญาติ)
    const relations = dataAsset.filter((item) => item.relationCustomerId);

    // ✅ ผู้ค้ำทั้งหมด
    const guarantors = dataAsset.filter(
      (item) => item.garno > 0 && !item.relationCustomerId,
    );

    // ✅ รวมทุกคน
    const allPeople = [
      mainBorrower?.customerId,
      ...relations.map((r) => r.customerId),
      ...guarantors.map((g) => g.customerId),
    ].filter(Boolean);

    if (allPeople.length === 0) return false;

    const allChecked = allPeople.every((id) => checkedList[`id_card_${id}`]);

    console.log("✅ คำนวณ idCardsChecked:", {
      totalPeople: allPeople.length,
      mainBorrower: mainBorrower?.customerId,
      relations: relations.length,
      guarantors: guarantors.length,
      allChecked,
    });

    return allChecked;
  }, [dataAsset, checkedList]);

  // 2. บอกกล่าว - เช็คจากจำนวน WCC ที่ flagStatus === 1
  const noticeChecked = useMemo(() => {
    const wccNotice = dataWCC.filter((item) => item.flagStatus === 1);
    const allNoticeIds = wccNotice.map((item) => item.id);

    if (allNoticeIds.length === 0) return false;

    const allChecked = allNoticeIds.every((id) => checkedList[`notice_${id}`]);

    console.log("✅ คำนวณ noticeChecked:", {
      totalNotices: allNoticeIds.length,
      allChecked,
    });

    return allChecked;
  }, [dataWCC, checkedList]);

  // 3. บอกเลิก - เช็คจากจำนวน WCC ทั้งหมด
  const terminationChecked = useMemo(() => {
    const allTerminationIds = dataWCC.map((item) => item.id);

    if (allTerminationIds.length === 0) return false;

    const allChecked = allTerminationIds.every(
      (id) => checkedList[`termination_${id}`],
    );

    console.log("✅ คำนวณ terminationChecked:", {
      totalTerminations: allTerminationIds.length,
      allChecked,
    });

    return allChecked;
  }, [dataWCC, checkedList]);

  // 4. ทร.14 - เช็คจากที่อยู่ล่าสุดของแต่ละ customer
  const tr14Checked = useMemo(() => {
    const tr14List = getTr14List();

    // รวบรวม ID ของที่อยู่ทั้งหมด
    const allAddressIds = [];
    tr14List.forEach((person) => {
      person.addressTr.forEach((addr) => {
        allAddressIds.push(`tr14_${person.customerId}_${addr.addrTrId}`);
      });
    });

    if (allAddressIds.length === 0) return false;

    const allChecked = allAddressIds.every((id) => checkedList[id]);

    console.log("✅ คำนวณ tr14Checked:", {
      totalAddresses: allAddressIds.length,
      allChecked,
    });

    return allChecked;
  }, [getTr14List, checkedList]);

  const getCollapseItems = () => {
    const items = [];

    // 1. สัญญา (ไม่สามารถกดเปิดได้)
    items.push({
      key: "contract",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={checkedList["contract"]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                handleCheckboxChange("contract", e.target.checked)
              }
              defaultChecked={true}
            />
            <FileTextOutlined />
            <Typography.Text strong>สัญญา</Typography.Text>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["contract_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["contract_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["contract_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      collapsible: "disabled",
      showArrow: false,
    });

    // 2. บอกกล่าว (มี nested checkbox)
    const wccNotice = dataWCC.filter((item) => item.flagStatus === 1);
    items.push({
      key: "notice",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={noticeChecked}
              onClick={(e) => e.stopPropagation()}
              disabled
            />
            <FileTextOutlined />
            <Typography.Text strong>
              บอกกล่าว (ตัวจริง)
              <Tag
                color={
                  dataWCC?.every((item) => item.status === 1) ? "green" : "red"
                }
                style={{ marginLeft: 8 }}
              >
                {wccNotice.length} รายการ
              </Tag>
            </Typography.Text>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["notice_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["notice_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["notice_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      children: (
        <Card size="small">
          {wccNotice.length > 0 ? (
            wccNotice.map((item, index) => (
              <div key={item.id} style={{ marginBottom: 8 }}>
                <Space>
                  <Checkbox
                    checked={checkedList[`notice_${item.id}`]}
                    onChange={(e) =>
                      handleCheckboxChange(
                        `notice_${item.id}`,
                        e.target.checked,
                      )
                    }
                  />
                  <Typography.Text>
                    {index + 1}. {item.customerFullname}
                  </Typography.Text>
                </Space>
                <div style={{ marginLeft: 40 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    เลขพัสดุ: {item.parcelNo}
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    วันที่ตอบกลับ:{" "}
                    {dayjs(item.dateResponse).format("DD/MM/YYYY") || "-"}
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    สถานะตอบกลับ:{" "}
                    {item.status === 1
                      ? "ใบตอบกลับ"
                      : item.status === 2
                        ? "จากเว็บ"
                        : item.status === 3
                          ? "ตีกลับ"
                          : "-"}
                  </Typography.Text>
                </div>
                {index < wccNotice.length - 1 && (
                  <Divider style={{ margin: "8px 0" }} />
                )}
              </div>
            ))
          ) : (
            <Empty
              description="ไม่มีข้อมูลบอกกล่าว"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    });

    // 3. บอกเลิก (มี nested checkbox)
    items.push({
      key: "termination",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={terminationChecked}
              onClick={(e) => e.stopPropagation()}
              disabled
            />
            <FileTextOutlined />
            <Typography.Text strong>
              บอกเลิก (ตัวจริง){" "}
              <Tag
                color={
                  dataWCC?.every((item) => item.status === 1) ? "green" : "red"
                }
                style={{ marginLeft: 8 }}
              >
                {dataWCC.length}
                รายการ
              </Tag>
            </Typography.Text>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["termination_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["termination_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["termination_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      children: (
        <Card size="small">
          {dataWCC.length > 0 ? (
            dataWCC.map((item, index) => (
              <div key={item.id} style={{ marginBottom: 8 }}>
                <Space>
                  <Checkbox
                    checked={checkedList[`termination_${item.id}`]}
                    onChange={(e) =>
                      handleCheckboxChange(
                        `termination_${item.id}`,
                        e.target.checked,
                      )
                    }
                  />
                  <Typography.Text>
                    {index + 1}. {item.customerFullname}
                  </Typography.Text>
                </Space>
                <div style={{ marginLeft: 40 }}>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    เลขพัสดุ: {item.parcelNo}
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    วันที่ตอบกลับ:{" "}
                    {dayjs(item.dateResponse).format("DD/MM/YYYY") || "-"}
                  </Typography.Text>
                  <br />
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    สถานะตอบกลับ:{" "}
                    {item.status === 1
                      ? "ใบตอบกลับ"
                      : item.status === 2
                        ? "จากเว็บ"
                        : item.status === 3
                          ? "ตีกลับ"
                          : "-"}
                  </Typography.Text>
                </div>
                {index < dataWCC.length - 1 && (
                  <Divider style={{ margin: "8px 0" }} />
                )}
              </div>
            ))
          ) : (
            <Empty
              description="ไม่มีข้อมูลบอกเลิก"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    });

    // 4. เอกสารหลักทรัพย์ (ไม่สามารถกดเปิดได้)
    items.push({
      key: "property_docs",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={checkedList["property_docs"]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                handleCheckboxChange("property_docs", e.target.checked)
              }
              defaultChecked={true}
            />
            <HomeOutlined />
            <Typography.Text strong>
              เอกสารหลักทรัพย์ (หน้าเล่ม,เอกสารจำนอง)
            </Typography.Text>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["property_docs_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["property_docs_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["property_docs_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      collapsible: "disabled",
      showArrow: false,
    });

    // 5. การ์ดลูกหนี้ (ไม่สามารถกดเปิดได้)
    items.push({
      key: "debtor_card",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={checkedList["debtor_card"]}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) =>
                handleCheckboxChange("debtor_card", e.target.checked)
              }
              defaultChecked={true}
            />
            <IdcardOutlined />
            <Link strong onClick={() => setIsModalDebtPayment(true)}>
              การ์ดลูกหนี้ (ระบบใหม่)
            </Link>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["debtor_card_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["debtor_card_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["debtor_card_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      collapsible: "disabled",
      showArrow: false,
    });

    // 6. สำเนาบัตร ทุกคน (มี nested checkbox)
    const mainBorrower = dataAsset.find(
      (item) => item.garno === 0 && !item.relationCustomerId,
    );

    const borrowerRelations = dataAsset.filter(
      (item) => item.relationCustomerId === mainBorrower?.customerId,
    );

    const mainGuarantors = dataAsset.filter(
      (item) => item.garno > 0 && !item.relationCustomerId,
    );

    items.push({
      key: "id_cards",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={idCardsChecked}
              onClick={(e) => e.stopPropagation()}
              disabled
            />
            <IdcardOutlined />
            <Typography.Text strong>
              สำเนาบัตรประชาชน{" "}
              <Tag color="cyan" style={{ marginLeft: 8 }}>
                {dataAsset.length} คน
              </Tag>
            </Typography.Text>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["id_cards_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["id_cards_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["id_cards_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),
      children: (
        <Card size="small">
          {/* ✅ 1. ผู้เช่าซื้อและคนที่เกี่ยวข้อง */}
          <div style={{ marginBottom: 12 }}>
            <Typography.Text strong>1. ผู้เช่าซื้อ</Typography.Text>
            {mainBorrower ? (
              <>
                {/* ผู้เช่าซื้อหลัก */}
                <div style={{ marginLeft: 16, marginTop: 4 }}>
                  <Space>
                    <Checkbox
                      checked={
                        checkedList[`id_card_${mainBorrower.customerId}`]
                      }
                      onChange={(e) =>
                        handleCheckboxChange(
                          `id_card_${mainBorrower.customerId}`,
                          e.target.checked,
                        )
                      }
                    />
                    <Typography.Text>
                      {mainBorrower.SNAM}
                      {mainBorrower.NAME1} {mainBorrower.NAME2}
                    </Typography.Text>
                  </Space>
                  <div style={{ marginLeft: 40 }}>
                    <Typography.Text type="secondary" style={{ fontSize: 14 }}>
                      เลขบัตรประชาชน: {mainBorrower.CUSCOD}
                    </Typography.Text>
                  </div>
                </div>

                {/* ✅ คนที่เกี่ยวข้องกับผู้เช่าซื้อ (คู่สมรส/ญาติ) */}
                {borrowerRelations.length > 0 &&
                  borrowerRelations.map((relation) => (
                    <div
                      key={relation.customerId}
                      style={{ marginLeft: 40, marginTop: 8 }}
                    >
                      <Space>
                        <Checkbox
                          checked={
                            checkedList[`id_card_${relation.customerId}`]
                          }
                          onChange={(e) =>
                            handleCheckboxChange(
                              `id_card_${relation.customerId}`,
                              e.target.checked,
                            )
                          }
                        />
                        <Typography.Text>
                          {relation.SNAM}
                          {relation.NAME1} {relation.NAME2}
                          <Tag color="green" style={{ marginLeft: 8 }}>
                            {relation.relation}
                          </Tag>
                        </Typography.Text>
                      </Space>
                      <div style={{ marginLeft: 40 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 14 }}
                        >
                          เลขบัตรประชาชน: {relation.CUSCOD}
                        </Typography.Text>
                      </div>
                    </div>
                  ))}
              </>
            ) : (
              <Typography.Text type="secondary">ไม่พบข้อมูล</Typography.Text>
            )}
          </div>

          <Divider style={{ margin: "12px 0" }} />

          {/* ✅ 2. ผู้ค้ำและคนที่เกี่ยวข้อง */}
          <div>
            <Typography.Text strong>
              2. คนค้ำประกัน ({mainGuarantors.length} คน)
            </Typography.Text>
            {mainGuarantors.length > 0 ? (
              mainGuarantors.map((guarantor, index) => {
                // ✅ หาคนที่เกี่ยวข้องกับผู้ค้ำคนนี้
                const guarantorRelations = dataAsset.filter(
                  (item) => item.relationCustomerId === guarantor.customerId,
                );

                return (
                  <div key={guarantor.customerId} style={{ marginTop: 8 }}>
                    {/* ผู้ค้ำหลัก */}
                    <div style={{ marginLeft: 16 }}>
                      <Space>
                        <Checkbox
                          checked={
                            checkedList[`id_card_${guarantor.customerId}`]
                          }
                          onChange={(e) =>
                            handleCheckboxChange(
                              `id_card_${guarantor.customerId}`,
                              e.target.checked,
                            )
                          }
                        />
                        <Typography.Text>
                          {index + 1}. {guarantor.SNAM}
                          {guarantor.NAME1} {guarantor.NAME2}
                        </Typography.Text>
                      </Space>
                      <div style={{ marginLeft: 40 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 14 }}
                        >
                          เลขบัตรประชาชน: {guarantor.CUSCOD}
                        </Typography.Text>
                      </div>
                    </div>

                    {/* ✅ คนที่เกี่ยวข้องกับผู้ค้ำ (ถ้ามี) */}
                    {guarantorRelations.length > 0 &&
                      guarantorRelations.map((relation) => (
                        <div
                          key={relation.customerId}
                          style={{ marginLeft: 40, marginTop: 8 }}
                        >
                          <Space>
                            <Checkbox
                              checked={
                                checkedList[`id_card_${relation.customerId}`]
                              }
                              onChange={(e) =>
                                handleCheckboxChange(
                                  `id_card_${relation.customerId}`,
                                  e.target.checked,
                                )
                              }
                            />
                            <Typography.Text>
                              {relation.SNAM}
                              {relation.NAME1} {relation.NAME2}
                              <Tag color="green" style={{ marginLeft: 8 }}>
                                {relation.relation}
                              </Tag>
                            </Typography.Text>
                          </Space>
                          <div style={{ marginLeft: 40 }}>
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 14 }}
                            >
                              เลขบัตรประชาชน: {relation.CUSCOD}
                            </Typography.Text>
                          </div>
                        </div>
                      ))}

                    {index < mainGuarantors.length - 1 && (
                      <Divider style={{ margin: "12px 0" }} />
                    )}
                  </div>
                );
              })
            ) : (
              <Typography.Text type="secondary">ไม่มีคนค้ำ</Typography.Text>
            )}
          </div>
        </Card>
      ),
    });

    // 8. ทร.14
    const tr14List = getTr14List();

    // นับจำนวนที่อยู่ทั้งหมด
    const totalTr14Addresses = tr14List.reduce(
      (sum, person) => sum + person.addressTr.length,
      0,
    );

    items.push({
      key: "tr14",
      label: (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Space>
            <Checkbox
              checked={tr14Checked}
              onClick={(e) => e.stopPropagation()}
              disabled
            />
            <FileTextOutlined />
            <Typography.Text strong>
              ทร.14
              <Tag color="purple" style={{ marginLeft: 8 }}>
                {totalTr14Addresses} ที่อยู่
              </Tag>
            </Typography.Text>
            <Button
              type="dashed"
              style={{ color: "blue" }}
              icon={<PlusOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                setIsModalAddAddressTr(true);
              }}
            >
              เพิ่มที่อยู่ ทร.14
            </Button>
          </Space>
          {status === 6 && (
            <Checkbox
              checked={verifyList["tr14_verify"]}
              onClick={(e) => e.stopPropagation()}
              style={{ marginRight: 16 }}
              disabled
            >
              {verifyList["tr14_verify"] ? (
                <CheckOutlined style={{ color: "#52c41a" }} />
              ) : (
                <CloseOutlined style={{ color: "#ff4d4f" }} />
              )}{" "}
              {verifyList["tr14_verify"] ? "พบเอกสาร" : "ไม่พบเอกสาร"}
            </Checkbox>
          )}
        </div>
      ),

      children: (
        <Card size="small">
          {tr14List.length > 0 ? (
            tr14List.map((item, personIndex) => {
              const address = item.addressTr[0];
              const addressId = `tr14_${item.customerId}_${address.addrTrId}`;
              const isRecent = dayjs(address.CreatedAt).isAfter(
                dayjs().subtract(30, "day"),
              );

              return (
                <div
                  key={`${item.customerId}_${personIndex}`}
                  style={{ marginBottom: 16 }}
                >
                  <Typography.Text strong>
                    {personIndex + 1}. {item.name}
                    {item.type === "guarantor" && (
                      <Tag color="green" style={{ marginLeft: 8 }}>
                        {item.relation}
                      </Tag>
                    )}
                  </Typography.Text>
                  <br />

                  <div style={{ marginLeft: 16, marginTop: 8 }}>
                    <Space align="start">
                      <Checkbox
                        checked={checkedList[addressId]}
                        onChange={(e) =>
                          handleCheckboxChange(addressId, e.target.checked)
                        }
                        style={{ marginTop: 2 }}
                      />
                      <div style={{ flex: 1 }}>
                        <Typography.Text style={{ fontSize: 13 }}>
                          <strong>ที่อยู่ล่าสุด:</strong>{" "}
                          {address.fullAddress || "ไม่มีข้อมูล"}
                        </Typography.Text>
                        <br />

                        {address.houseRegistrationNumber && (
                          <>
                            <Typography.Text
                              type="secondary"
                              style={{ fontSize: 12 }}
                            >
                              📋 เลขทะเบียนบ้าน:{" "}
                              {address.houseRegistrationNumber}
                            </Typography.Text>
                            <br />
                          </>
                        )}

                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          📍{" "}
                          {[
                            address.subdistrict && `ต.${address.subdistrict}`,
                            address.district && `อ.${address.district}`,
                            address.province && `จ.${address.province}`,
                            address.zipcode,
                          ]
                            .filter(Boolean)
                            .join(" ")}
                        </Typography.Text>
                        <br />

                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          🕒 คัดเมื่อ:{" "}
                          <Tag color={isRecent ? "green" : "red"}>
                            {dayjs(address.CreatedAt).format("DD/MM/YYYY")}{" "}
                          </Tag>
                          <Tag color={isRecent ? "green" : "red"}>
                            {isRecent ? "ไม่เกิน 1 เดือน" : "เกิน 1 เดือน"}
                          </Tag>
                        </Typography.Text>
                      </div>
                    </Space>
                  </div>

                  {personIndex < tr14List.length - 1 && (
                    <Divider style={{ margin: "12px 0" }} />
                  )}
                </div>
              );
            })
          ) : (
            <Empty
              description="ไม่มีข้อมูล ทร.14 ที่คัดไม่เกิน 1 เดือน"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )}
        </Card>
      ),
    });

    return items;
  };

  // ✅ สร้างข้อความแสดงชื่อผู้เช่าซื้อและผู้ค้ำ
  const getHeaderInfo = () => {
    const borrower = dataAsset.find((item) => item.garno === 0);
    const guarantors = dataAsset.filter((item) => item.garno > 0);

    return (
      <div>
        <div>
          <Typography.Text strong>เลขที่สัญญา: </Typography.Text>
          <Tag color="blue">{dataDefault?.CONTNO}</Tag>
          {status === 3 ? (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              แก้ไข
            </Tag>
          ) : status === 6 ? (
            <Tag color="orange" style={{ marginLeft: 8 }}>
              เอกสารตีกลับ
            </Tag>
          ) : null}
        </div>
        {borrower && (
          <div style={{ marginTop: 4 }}>
            <Typography.Text strong>ผู้เช่าซื้อ: </Typography.Text>
            <Typography.Text>
              {borrower.SNAM}
              {borrower.NAME1} {borrower.NAME2}
            </Typography.Text>
          </div>
        )}
        {guarantors.length > 0 && (
          <div style={{ marginTop: 4 }}>
            <Typography.Text strong>ผู้ค้ำประกัน: </Typography.Text>
            {guarantors.map((guarantor, index) => (
              <span key={guarantor.customerId}>
                {guarantor.SNAM}
                {guarantor.NAME1} {guarantor.NAME2}
                {index < guarantors.length - 1 ? ", " : ""}
              </span>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a" }} />
          <span>
            {status === 3
              ? "แก้ไขเอกสาร"
              : status === 6
                ? "เอกสารตีกลับ"
                : "ตรวจสอบเอกสาร"}
          </span>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      width={800}
      maskClosable={false}
      centered
      footer={[
        <Button key="back" onClick={handleCancel} danger>
          ปิด
        </Button>,
        <Popconfirm
          title="ยืนยัน"
          description="คุณต้องการบันทึกใช่หรือไม่ ?"
          onConfirm={handleSave}
          onCancel={null}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
        >
          <Button key="save" type="primary" loading={loading}>
            {status === 3 || status === 6 ? "บันทึกการแก้ไข" : "บันทึก"}
          </Button>
        </Popconfirm>,
      ]}
    >
      <div
        ref={scrollRef}
        style={{
          maxHeight: "70vh",
          overflowY: "auto",
          paddingRight: 8,
        }}
      >
        <Spin spinning={loading}>
          {dataLoadLawSuit ? (
            <>
              <Card
                size="small"
                style={{ marginBottom: 16, backgroundColor: "#f0f5ff" }}
              >
                {getHeaderInfo()}
              </Card>
              <Collapse
                items={getCollapseItems()}
                activeKey={activeKey}
                onChange={setActiveKey}
                style={{ marginBottom: 16 }}
              />
              {/* แสดงรายการหมายเหตุที่มีอยู่แล้ว */}
              {memoData?.length > 0 && (
                <>
                  <Divider orientation="left">
                    <Space>
                      <Typography.Text strong>หมายเหตุก่อนหน้า</Typography.Text>
                      <Tag color="blue">{memoData.length} รายการ</Tag>
                    </Space>
                  </Divider>
                  <List
                    style={{ marginBottom: 16 }}
                    bordered
                    dataSource={memoData}
                    renderItem={(item, index) => (
                      <List.Item>
                        <List.Item.Meta
                          // avatar={
                          //   <Avatar style={{ backgroundColor: "#1890ff" }}>
                          //     {item.USERNAME?.charAt(0).toUpperCase()}
                          //   </Avatar>
                          // }
                          title={
                            <Space>
                              <Typography.Text strong>
                                {item.USERNAME} :
                              </Typography.Text>
                              <Typography.Text
                                type="secondary"
                                style={{ fontSize: 12 }}
                              >
                                {dayjs(item.CreatedAt).format(
                                  "DD/MM/YYYY HH:mm",
                                )}
                              </Typography.Text>
                            </Space>
                          }
                          description={item.note}
                        />
                      </List.Item>
                    )}
                  />
                </>
              )}

              <Divider orientation="left">
                <Typography.Text strong>เพิ่มหมายเหตุใหม่</Typography.Text>
              </Divider>
              <Input.TextArea
                value={newMemoText}
                onChange={(e) => setNewMemoText(e.target.value)}
                rows={3}
                placeholder="กรอกหมายเหตุใหม่..."
                maxLength={500}
                showCount
              />
            </>
          ) : (
            <Empty description="ไม่มีข้อมูล" />
          )}
        </Spin>

        <FloatButton.BackTop
          target={() => scrollRef.current}
          visibilityHeight={80}
          style={{ right: 16, bottom: 16 }}
        />
      </div>

      {isModalAddAddressTr && (
        <AddAddressTr
          open={isModalAddAddressTr}
          close={() => setIsModalAddAddressTr(false)}
          dataDefault={dataDefault}
          func={handleLoad}
          geoData={geoRawData}
        />
      )}
      {isModalDebtPayment && (
        <DebtPayment
          open={isModalDebtPayment}
          close={() => setIsModalDebtPayment(false)}
          initialContractNo={dataDefault.CONTNO}
          initialDataType={null}
          dataDefault={dataDefault}
        />
      )}
    </Modal>
  );
};

export default CheckingDocuments;
